import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useCheckoutDispatch, useCheckoutState } from '@olo-web/client-state';
import { useDisclosure } from '@chakra-ui/hooks';
import { AddButtonWithIcon } from '../common';
import { Textarea, VStack, Flex } from '@chakra-ui/react';
import { SavedIndicator } from '@olo-web/components/atoms/SavedIndicator';
import { MotionCollapse } from '@olo-web/components/atoms/MotionCollapse';
import { useOrderTypeDisplayName, useDebounce, useToast } from '@olo-web/utils/common/hooks';

export const OrderNote = () => {
  const { orderNote } = useCheckoutState();
  const orderTypeDisplayName = useOrderTypeDisplayName();
  const checkoutDispatch = useCheckoutDispatch();
  const [value, setValue] = useState(orderNote || '');
  const { notify, toast } = useToast();
  const inputRef = useRef(null);
  const textRef = useRef(null);

  const debouncedValue = useDebounce(value);

  const { isOpen, onClose, onOpen } = useDisclosure();

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
  };

  useEffect(() => {
    checkoutDispatch({
      type: 'SET_ORDER_NOTE',
      payload: debouncedValue,
    });
  }, [checkoutDispatch, debouncedValue]);

  const toastId = 'order-note';

  const handleToggle = () => {
    if (value) {
      if (!toast.isActive(toastId)) {
        notify({
          id: toastId,
          title: 'Delete your order note',
          description: 'Then you can close the text field',
          variant: 'left-accent',
          status: 'info',
          position: 'top',
          isClosable: true,
        });
      }
      return;
    }
    if (isOpen) {
      onClose();
      return;
    }
    onOpen();
    setTimeout(() => inputRef?.current?.focus(), 250);
  };

  const label = useMemo(() => {
    return `Add ${orderTypeDisplayName.toLowerCase()} note`;
  }, [orderTypeDisplayName]);

  const open = isOpen || !!orderNote?.length;

  return (
    <VStack spacing={0} w="100%" alignItems="stretch" data-testid="add-order-note" ref={textRef}>
      <AddButtonWithIcon
        label={label}
        onClick={handleToggle}
        isOpen={open}
        aria-expanded={open}
        aria-controls={open ? 'collapse_ordernote' : undefined}
        labelProps={{
          color: 'blackAlpha.500',
        }}
        pl={0}
        mt={-2}
        mb={-3}
      />
      <MotionCollapse isOpen={open} id="collapse_ordernote">
        <Flex flexDir="column" align="stretch">
          <Textarea
            mt={3}
            noOfLines={3}
            resize="none"
            onChange={handleChange}
            defaultValue={orderNote}
            autoFocus={isOpen}
            placeholder={`${label}...`}
            ref={inputRef}
            data-testid="add-order-note-input"
            onFocus={() => {
              setTimeout(() => {
                textRef.current?.scrollIntoView({ block: 'start' });
              }, 250);
            }}
          />
          <Flex alignSelf="flex-end" mt={2} mr={1}>
            <SavedIndicator
              isLoading={debouncedValue && value !== debouncedValue}
              isSaved={debouncedValue && value === debouncedValue}
            />
          </Flex>
        </Flex>
      </MotionCollapse>
    </VStack>
  );
};
